<template>
  <Teleport to="body">
    <transition name="savenote">
      <div
        v-if="dialog"
        class="savenote"
        role="dialog"
        aria-modal="true"
        @mousedown.self="$emit('close')"
      >
        <div class="savenote__panel">
          <header class="savenote__head">
            <h3 class="savenote__title">
              {{ isEdit ? "Notitie bewerken" : "Bewaren" }}
            </h3>
            <button
              type="button"
              class="savenote__close"
              title="Sluiten"
              @click="$emit('close')"
            >
              ×
            </button>
          </header>

          <div class="savenote__body">
            <label class="savenote__field">
              <span class="savenote__label">Tekst</span>
              <textarea
                ref="textRef"
                v-model="text"
                class="savenote__textarea"
                rows="6"
              ></textarea>
            </label>

            <label class="savenote__field">
              <span class="savenote__label">Label</span>
              <input
                v-model="label"
                class="savenote__input"
                type="text"
                list="savenote-labels"
                placeholder="bv. conclusie argument, input principes"
              />
              <datalist id="savenote-labels">
                <option v-for="l in existingLabels" :key="l" :value="l" />
              </datalist>
              <span class="savenote__hint"
                >Kies een bestaand label of typ een nieuw label</span
              >
            </label>

            <label class="savenote__field">
              <span class="savenote__label">Notitie (optioneel)</span>
              <textarea
                v-model="noteText"
                class="savenote__textarea"
                rows="2"
              ></textarea>
            </label>
          </div>

          <footer class="savenote__foot">
            <button
              type="button"
              class="savenote__cancel"
              @click="$emit('close')"
            >
              Annuleren
            </button>
            <button
              type="button"
              class="savenote__save"
              :disabled="!canSave"
              @click="save"
            >
              Bewaren
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  name: "SaveNoteDialog",
  props: {
    dialog: { type: Boolean, default: false },
    // When set, the dialog edits this existing note; otherwise it creates one.
    note: { type: Object, default: null },
    // Pre-filled text + source for a brand new note (from a chat answer).
    draft: { type: Object, default: null },
    existingLabels: { type: Array, default: () => [] },
  },
  emits: ["close", "save"],
  data() {
    return {
      text: "",
      label: "",
      noteText: "",
    };
  },
  computed: {
    isEdit() {
      return !!this.note;
    },
    canSave() {
      return !!String(this.text || "").trim();
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        this.hydrate();
        document.addEventListener("keydown", this.onKeydown);
        this.$nextTick(() => {
          if (this.$refs.textRef) this.$refs.textRef.focus();
        });
      } else {
        document.removeEventListener("keydown", this.onKeydown);
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === "Escape") this.$emit("close");
    },
    hydrate() {
      const src = this.note || this.draft || {};
      this.text = src.text || "";
      this.label = src.label || "";
      this.noteText = src.note || "";
    },
    save() {
      if (!this.canSave) return;
      this.$emit("save", {
        uid: this.note?.uid || null,
        text: String(this.text || "").trim(),
        label: String(this.label || "").trim(),
        note: String(this.noteText || "").trim() || null,
        source: this.note?.source ?? this.draft?.source ?? null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.savenote {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20, 25, 25, 0.55);

  &__panel {
    width: 100%;
    max-width: 640px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    background: var(--vp-white);
    border: 1px solid var(--vp-black);
    border-radius: 0;
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    background: var(--vp-black);
    color: var(--vp-white);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1;
  }

  &__close {
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--font-display);
    font-size: 26px;
    line-height: 1;
    color: var(--vp-white);
    opacity: 0.8;
    transition: opacity var(--dur-fast) var(--ease-hover);

    &:hover {
      opacity: 1;
    }
  }

  &__body {
    padding: 24px 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-family: var(--font-ui);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-3);
  }

  &__input,
  &__textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    outline: none;
    background: var(--vp-white);
    font-family: var(--font-body);
    font-size: 15px;
    line-height: 1.5;
    color: var(--vp-black);
    transition: border-color var(--dur-fast) var(--ease-hover);

    &:focus {
      border-color: var(--vp-black);
    }

    &::placeholder {
      color: var(--fg-3);
    }
  }

  &__textarea {
    resize: vertical;
    min-height: 48px;
  }

  &__hint {
    font-family: var(--font-ui);
    font-size: 12px;
    color: var(--fg-3);
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid var(--border-hairline);
  }

  &__cancel {
    padding: 8px 16px;
    background: var(--vp-white);
    border: 1px solid var(--vp-black);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--vp-black);
    transition: background var(--dur-fast) var(--ease-hover),
      color var(--dur-fast) var(--ease-hover),
      transform var(--dur-press) var(--ease-out);

    &:hover {
      background: var(--vp-black);
      color: var(--vp-white);
    }

    &:active {
      transform: scale(0.97);
    }
  }

  &__save {
    padding: 8px 18px;
    background: var(--vp-black);
    color: var(--vp-white);
    border: 1px solid var(--vp-black);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    transition: background var(--dur-fast) var(--ease-hover),
      color var(--dur-fast) var(--ease-hover),
      transform var(--dur-press) var(--ease-out);

    &:hover:not(:disabled) {
      background: var(--accent-100);
      color: var(--vp-black);
    }

    &:active:not(:disabled) {
      transform: scale(0.97);
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}

/* Entrance: backdrop fades, panel scales up from 0.96. Modal stays centered
   (no trigger to anchor to). Exit is faster than enter. */
.savenote-enter-active {
  transition: opacity var(--dur-moderate) var(--ease-out);

  .savenote__panel {
    transition: transform var(--dur-moderate) var(--ease-out),
      opacity var(--dur-moderate) var(--ease-out);
  }
}
.savenote-leave-active {
  transition: opacity var(--dur-exit) var(--ease-out);

  .savenote__panel {
    transition: transform var(--dur-exit) var(--ease-out),
      opacity var(--dur-exit) var(--ease-out);
  }
}
.savenote-enter-from,
.savenote-leave-to {
  opacity: 0;

  .savenote__panel {
    opacity: 0;
    transform: scale(0.96);
  }
}
</style>
